import { createSlice } from '@reduxjs/toolkit';
import { NeglectedOrDelinquentContactModel } from '../../types/NeglectedOrDelinquentContactModel';
import { NeglectedOrDelinquentInformationModel } from '../../types/NeglectedOrDelinquentInformationModel';
import { SelectOption } from '../../types/SelectOption';
import {
    createNeglectedOrDelinquentContact,
    fetchNeglectedOrDelinquentContact,
    fetchNeglectedOrDelinquentSchoolYears,
    updateNeglectedOrDelinquentContact,
    createNeglectedOrDelinquentInformation,
    fetchNeglectedOrDelinquentInformation,
    fetchNeglectedOrDelinquentInputOptionSets,
    updateNeglectedOrDelinquentInformation,
} from '../thunks/neglectedOrDelinquentThunks';

type RequestStatus = 'idle' | 'pending' | 'succeeded' | 'failed';

interface NeglectedOrDelinquentState {
    contact: NeglectedOrDelinquentContactModel | null;
    schoolYears: SelectOption[];
    contactStatus: RequestStatus;
    schoolYearsStatus: RequestStatus;
    contactRequestId?: string;
    schoolYearsRequestId?: string;
    information: NeglectedOrDelinquentInformationModel | null;
    inputOptionSets: Record<string, SelectOption[]>;
    informationStatus: RequestStatus;
    inputOptionSetsStatus: RequestStatus;
}

export const initialState: NeglectedOrDelinquentState = {
    contact: null,
    schoolYears: [],
    contactStatus: 'idle',
    schoolYearsStatus: 'idle',
    information: null,
    inputOptionSets: {},
    informationStatus: 'idle',
    inputOptionSetsStatus: 'idle',
};

export const neglectedOrDelinquentSlice = createSlice({
    name: 'neglectedOrDelinquent',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchNeglectedOrDelinquentContact.pending, (state, action) => {
            state.contact = null;
            state.contactStatus = 'pending';
            state.contactRequestId = action.meta.requestId;
        });
        builder.addCase(
            fetchNeglectedOrDelinquentContact.fulfilled,
            (state, action) => {
                if (state.contactRequestId !== action.meta.requestId) return;
                state.contact = action.payload;
                state.contactStatus = 'succeeded';
                state.contactRequestId = undefined;
            }
        );
        builder.addCase(fetchNeglectedOrDelinquentContact.rejected, (state, action) => {
            if (state.contactRequestId !== action.meta.requestId) return;
            state.contactStatus = action.meta.aborted ? 'idle' : 'failed';
            state.contactRequestId = undefined;
        });
        builder.addCase(fetchNeglectedOrDelinquentSchoolYears.pending,
            (state, action) => {
                state.schoolYears = [];
                state.schoolYearsStatus = 'pending';
                state.schoolYearsRequestId = action.meta.requestId;
            });
        builder.addCase(
            fetchNeglectedOrDelinquentSchoolYears.fulfilled,
            (state, action) => {
                if (state.schoolYearsRequestId !== action.meta.requestId) return;
                state.schoolYears = action.payload;
                state.schoolYearsStatus = 'succeeded';
                state.schoolYearsRequestId = undefined;
            }
        );
        builder.addCase(fetchNeglectedOrDelinquentSchoolYears.rejected,
            (state, action) => {
                if (state.schoolYearsRequestId !== action.meta.requestId) return;
                state.schoolYearsStatus = action.meta.aborted ? 'idle' : 'failed';
                state.schoolYearsRequestId = undefined;
            });
        builder.addCase(
            createNeglectedOrDelinquentContact.fulfilled,
            (state, action) => {
                state.contact = action.payload;
            }
        );
        builder.addCase(
            updateNeglectedOrDelinquentContact.fulfilled,
            (state, action) => {
                state.contact = action.payload;
            }
        );
        builder.addCase(fetchNeglectedOrDelinquentInformation.pending, (state) => {
            state.information = null;
            state.informationStatus = 'pending';
        });
        builder.addCase(fetchNeglectedOrDelinquentInformation.fulfilled,
            (state, action) => {
                state.information = action.payload;
                state.informationStatus = 'succeeded';
            });
        builder.addCase(fetchNeglectedOrDelinquentInformation.rejected,
            (state, action) => {
                if (!action.meta.aborted) state.informationStatus = 'failed';
            });
        builder.addCase(fetchNeglectedOrDelinquentInputOptionSets.pending, (state) => {
            state.inputOptionSets = {};
            state.inputOptionSetsStatus = 'pending';
        });
        builder.addCase(fetchNeglectedOrDelinquentInputOptionSets.fulfilled,
            (state, action) => {
                state.inputOptionSets = action.payload.optionSets;
                state.inputOptionSetsStatus = 'succeeded';
            });
        builder.addCase(fetchNeglectedOrDelinquentInputOptionSets.rejected,
            (state, action) => {
                if (!action.meta.aborted) state.inputOptionSetsStatus = 'failed';
            });
        builder.addCase(createNeglectedOrDelinquentInformation.fulfilled,
            (state, action) => {
                state.information = action.payload;
            });
        builder.addCase(updateNeglectedOrDelinquentInformation.fulfilled,
            (state, action) => {
                state.information = action.payload;
            });
    },
});
